/*
 * FareRulesPanel.jsx — detailed Tripjack fare rules for Review / Booking / BookingDetails pages.
 *
 * Fetches GET /api/tripjack/fare-rules-detailed/{priceId}?flow_type={REVIEW|SEARCH|BOOKING_DETAIL}
 * when the supplier did not embed rules on /review, or when the user taps
 * “Refresh from airline”.
 *
 * Per Tripjack v2.0 PDF line 1084 the valid flowType values are:
 *   - SEARCH         — results page expand
 *   - REVIEW         — review / booking form (default)
 *   - BOOKING_DETAIL — post-booking details page
 *
 * Legacy alias `BOOKING` is accepted by the backend but callers should
 * prefer the spec-canonical values above.
 *
 * Returns stable normalised shape:
 *
 *   { success, raw, normalized: { routes, by_route: { <route>: {
 *       cancellation: [<slab>...],
 *       date_change:  [<slab>...],
 *       no_show: <slab|null>,
 *       seat_chargeable: <slab|null>,
 *       baggage: {checkin, cabin} | null
 *   } } } }
 *
 * Optional prop ``embeddedNormalizedFareRules`` supplies the same normalised
 * shape from POST ``/api/tripjack/review`` (``embedded_fare_rules_normalized``)
 * so the first expand can render without a cold GET when Tripjack already
 * sent ``totalPriceList[].fareRuleInformation``.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  ChevronDown, ChevronUp, Loader2, FileText, AlertCircle,
  RefreshCcw, Luggage, Ban, Armchair,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const SlabTable = ({ title, icon: Icon, slabs, accent = 'red' }) => {
  if (!slabs || slabs.length === 0) return null;

  const accentMap = {
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', head: 'bg-red-100' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', head: 'bg-orange-100' },
    gray: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', head: 'bg-gray-100' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', head: 'bg-blue-100' },
  };
  const c = accentMap[accent] || accentMap.red;

  return (
    <div className={`${c.bg} ${c.border} border rounded-lg overflow-hidden`} data-testid={`fare-rule-${accent}`}>
      <div className={`${c.head} px-4 py-2 flex items-center gap-2`}>
        {Icon && <Icon className={`w-4 h-4 ${c.text}`} />}
        <h4 className={`text-sm font-bold ${c.text}`}>{title}</h4>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/60">
            <tr className="text-gray-600">
              <th className="text-left px-3 py-2 font-semibold">Timeframe</th>
              <th className="text-right px-3 py-2 font-semibold">Airline Fee</th>
              <th className="text-right px-3 py-2 font-semibold">Platform Fee</th>
              <th className="text-right px-3 py-2 font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {slabs.map((s, i) => (
              <tr key={s.key + i} className="border-t border-white/80">
                <td className="px-3 py-2 text-gray-800">{s.timeframe_label}</td>
                <td className="px-3 py-2 text-right font-medium text-gray-800">{formatINR(s.amount)}</td>
                <td className="px-3 py-2 text-right text-gray-600">{formatINR(s.platform_fee)}</td>
                <td className={`px-3 py-2 text-right font-bold ${c.text}`}>{formatINR(s.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {slabs.some((s) => s.policy_info) && (
        <div className="px-4 py-2 bg-white/50 border-t border-white/80 space-y-1">
          {slabs
            .filter((s) => s.policy_info)
            .map((s, i) => (
              <p key={i} className="text-[11px] text-gray-600 leading-snug">
                <span className="font-semibold">{s.timeframe_label}: </span>
                {s.policy_info}
              </p>
            ))}
        </div>
      )}
    </div>
  );
};

const SingleFee = ({ title, icon: Icon, slab, accent = 'gray' }) => {
  if (!slab) return null;
  const accentMap = {
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
  };
  return (
    <div className={`${accentMap[accent]} border rounded-lg px-4 py-3 flex items-center justify-between`}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <span className="text-sm font-bold">{formatINR(slab.total)}</span>
    </div>
  );
};

const BaggageStrip = ({ baggage }) => {
  if (!baggage || (!baggage.checkin && !baggage.cabin)) return null;
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <Luggage className="w-4 h-4 text-emerald-700" />
        <span className="text-xs font-semibold text-emerald-700">Baggage Allowance</span>
      </div>
      {baggage.checkin && (
        <span className="text-xs text-emerald-900">
          <span className="font-semibold">Check-in:</span> {baggage.checkin}
        </span>
      )}
      {baggage.cabin && (
        <span className="text-xs text-emerald-900">
          <span className="font-semibold">Cabin:</span> {baggage.cabin}
        </span>
      )}
    </div>
  );
};

const _embeddedRulesUsable = (emb) =>
  emb && (
    (Array.isArray(emb.routes) && emb.routes.length > 0)
    || !!emb.unstructured_text
  );

const FareRulesPanel = ({
  priceId,
  priceIds,
  bookingId,
  flowType = 'REVIEW',
  /** Optional: normalised fare rules from POST /tripjack/review (`embedded_fare_rules_normalized`). */
  embeddedNormalizedFareRules = null,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [rules, setRules] = useState(null);
  /** 'embedded' | 'api' — when embedded, user can still refresh from /farerule. */
  const [rulesSource, setRulesSource] = useState(null);

  const embeddedSig = useMemo(
    () => JSON.stringify(embeddedNormalizedFareRules || null),
    [embeddedNormalizedFareRules],
  );
  const preferApiRef = useRef(false);

  // Per Tripjack v2.0 PDF line 1076-1078:
  //   flowType=SEARCH         → id must be a priceId (from /air-search-all)
  //   flowType=REVIEW         → id must be a bookingId (from /review)
  //   flowType=BOOKING_DETAIL → id must be a bookingId (from /booking-details)
  // Pick the right id based on the caller's flowType so Tripjack's /farerule
  // doesn't 404 when sent the wrong id shape.
  const upperFlow = String(flowType || '').toUpperCase();
  const needsBookingId = upperFlow === 'REVIEW'
    || upperFlow === 'BOOKING_DETAIL'
    || upperFlow === 'BOOKING';  // legacy alias
  const firstPriceId = Array.isArray(priceIds) && priceIds.length > 0
    ? priceIds[0]
    : priceId;
  const effectiveId = needsBookingId
    ? (bookingId || firstPriceId)   // fallback to priceId if bookingId missing
    : firstPriceId;

  useEffect(() => {
    setRules(null);
    setErr(null);
    setRulesSource(null);
    preferApiRef.current = false;
  }, [effectiveId, flowType, embeddedSig]);

  const fetchRules = useCallback(async () => {
    if (!effectiveId) return;
    setLoading(true);
    setErr(null);
    try {
      const resp = await axios.get(
        `${BACKEND_URL}/api/tripjack/fare-rules-detailed/${encodeURIComponent(effectiveId)}?flow_type=${flowType}`
      );
      if (resp.data?.success && resp.data.normalized) {
        setRules(resp.data.normalized);
        setRulesSource('api');
      } else {
        setErr(resp.data?.message || 'Fare rules unavailable.');
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Failed to load fare rules.');
    } finally {
      setLoading(false);
    }
  }, [effectiveId, flowType]);

  // Lazy-load on first expand: prefer review-embedded rules, else GET /farerule-backed detailed.
  useEffect(() => {
    if (!expanded || rules || err || loading) return;
    const emb = embeddedNormalizedFareRules;
    if (!preferApiRef.current && _embeddedRulesUsable(emb)) {
      setRules(emb);
      setRulesSource('embedded');
      return;
    }
    preferApiRef.current = false;
    fetchRules();
  }, [expanded, rules, err, loading, embeddedNormalizedFareRules, embeddedSig, fetchRules]);

  const loadFromAirline = () => {
    preferApiRef.current = true;
    setRules(null);
    setRulesSource(null);
    setErr(null);
  };

  if (!effectiveId) return null;

  const routes = rules?.routes || [];

  return (
    <div className="bg-gradient-to-br from-white to-amber-50 rounded-xl shadow-md overflow-hidden border border-amber-200" data-testid="fare-rules-panel">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between hover:from-amber-600 hover:to-orange-600 transition-colors"
        data-testid="fare-rules-toggle"
      >
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-white" />
          <span className="text-lg font-bold text-white">Cancellation &amp; Change Policy</span>
        </div>
        {expanded
          ? <ChevronUp className="w-5 h-5 text-white" />
          : <ChevronDown className="w-5 h-5 text-white" />}
      </button>

      {expanded && (
        <div className="p-4 sm:p-6 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-8" data-testid="fare-rules-loading">
              <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
              <span className="ml-2 text-sm text-gray-600">Loading fare rules…</span>
            </div>
          )}

          {!loading && rulesSource === 'embedded' && rules && (
            <div
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2"
              data-testid="fare-rules-embedded-hint"
            >
              <p className="text-[11px] text-amber-900 m-0 max-w-[min(100%,28rem)]">
                Showing rules included with your fare quote. Refresh for the latest from the airline.
              </p>
              <button
                type="button"
                onClick={loadFromAirline}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 hover:text-amber-950 whitespace-nowrap"
                data-testid="fare-rules-refresh-airline"
              >
                <RefreshCcw className="w-3 h-3" />
                Refresh from airline
              </button>
            </div>
          )}

          {!loading && err && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="fare-rules-error">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800">Fare rules unavailable</p>
                <p className="text-xs text-red-700 mt-1">
                  {String(err).length > 200 ? 'Please check the airline website for cancellation and change rules.' : String(err)}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    preferApiRef.current = true;
                    fetchRules();
                  }}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-700 hover:text-red-900"
                  data-testid="fare-rules-retry"
                >
                  <RefreshCcw className="w-3 h-3" />
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !err && routes.length === 0 && rules?.unstructured_text && (
            <div className="bg-amber-50/80 border border-amber-200 rounded-lg p-4" data-testid="fare-rules-cat16-only">
              <p className="text-xs font-semibold text-amber-900 mb-2">Fare rules (airline text)</p>
              <pre className="whitespace-pre-wrap break-words text-xs text-gray-800 font-sans leading-relaxed max-h-[min(480px,55vh)] overflow-y-auto">
                {rules.unstructured_text}
              </pre>
            </div>
          )}

          {!loading && !err && routes.length === 0 && !rules?.unstructured_text && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center" data-testid="fare-rules-empty">
              <p className="text-sm text-gray-600">No fare rules returned by the airline.</p>
              <p className="text-xs text-gray-500 mt-1">
                Please check the airline website before booking for cancellation and change policies.
              </p>
            </div>
          )}

          {!loading && !err && routes.map((route) => {
            const leg = rules.by_route[route];
            if (!leg) return null;
            return (
              <div key={route} className="space-y-3" data-testid={`fare-rules-route-${route}`}>
                {routes.length > 1 && (
                  <div className="inline-block bg-[#1B375B] text-white text-xs font-bold px-3 py-1 rounded-full">
                    {route}
                  </div>
                )}

                <BaggageStrip baggage={leg.baggage} />

                <SlabTable
                  title="Cancellation"
                  icon={Ban}
                  slabs={leg.cancellation}
                  accent="red"
                />
                <SlabTable
                  title="Date Change"
                  icon={RefreshCcw}
                  slabs={leg.date_change}
                  accent="orange"
                />
                <div className="grid sm:grid-cols-2 gap-3">
                  <SingleFee
                    title="No-show Fee"
                    icon={Ban}
                    slab={leg.no_show}
                    accent="gray"
                  />
                  <SingleFee
                    title="Seat Selection Charge"
                    icon={Armchair}
                    slab={leg.seat_chargeable}
                    accent="blue"
                  />
                </div>
              </div>
            );
          })}

          {!loading && !err && routes.length > 0 && rules?.unstructured_text && (
            <div className="bg-amber-50/60 border border-amber-100 rounded-lg p-4 space-y-2" data-testid="fare-rules-supplemental-text">
              <p className="text-xs font-semibold text-amber-900">Additional airline fare text</p>
              <pre className="whitespace-pre-wrap break-words text-[11px] text-gray-800 font-sans leading-relaxed max-h-[min(360px,40vh)] overflow-y-auto">
                {rules.unstructured_text}
              </pre>
            </div>
          )}

          {!loading && !err && routes.length > 0 && (
            <div className="text-[11px] text-gray-500 space-y-0.5 pt-2 border-t border-amber-100">
              <p>• Fees shown are <span className="font-semibold">per passenger per sector</span>.</p>
              <p>• Airline fees are indicative and depend on the actual time of cancellation / re-issue per the airline&apos;s live fare rules.</p>
              <p>• T&amp;C apply. Please review the airline&apos;s policy before booking.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FareRulesPanel;
